import { z } from "zod";
import type { FieldPath } from "react-hook-form";
import type { CrmRules } from "@/lib/api/crm/rules";
import {
  crmErrorReason,
  crmValidationIssues,
  isCrmAuthError,
  type CrmValidationIssue,
  type FailedResult,
} from "../crmErrors";
import { CRM_INVALID_PHONE_MESSAGE } from "@/lib/validation/crm";
import { addressTextFields, recordFormFields, type RecordFormValues } from "./recordFormSchema";
import type { RowIndexMap } from "./recordFormValues";
import { onePerContactKindMessage, tagNames } from "./tagOptions";

type RecordFormPath = FieldPath<RecordFormValues>;

export interface FormBanner {
  message: string;
  /** Offer "Load their changes" (refetch and reset) and "Keep editing". */
  offerReload?: boolean;
  /** Offer a link back to the CRM list. */
  offerBackToList?: boolean;
}

export interface FormFieldError {
  name: RecordFormPath;
  message: string;
}

export interface FormSubmitError {
  banner: FormBanner | null;
  fieldErrors: FormFieldError[];
  /** Tags were rejected: reload GET /crm/rules so the pickers update. */
  refetchRules?: boolean;
}

const TagIdsDataSchema = z.object({ tag_ids: z.array(z.string()) });
const DuplicateContactTagsDataSchema = z.object({
  duplicates: z.array(z.object({ tag_id: z.string(), kind: z.enum(["phone", "email"]) })),
});
const ContactIdsDataSchema = z.object({ contact_ids: z.array(z.string()) });
const AddressIdsDataSchema = z.object({ address_ids: z.array(z.string()) });

const RECORD_BASE_FIELDS = new Set<string>(["record_type", "owner_id", "client_id", "short_name", "long_name", "tag_ids"]);
const CONTACT_ROW_FIELDS = new Set<string>(["kind", "value", "label", "tag_ids"]);
const ADDRESS_ROW_FIELDS = new Set<string>([...addressTextFields, "tag_ids"]);

type RowSection = "contacts" | "addresses";

function rowPath(section: RowSection, index: number, field: string | null): RecordFormPath {
  const rowFields = section === "contacts" ? CONTACT_ROW_FIELDS : ADDRESS_ROW_FIELDS;
  // Row-level messages sit on the row's id.
  const name = field !== null && rowFields.has(field) ? field : "id";
  return `${section}.${index}.${name}` as RecordFormPath;
}

/** The form field a request path points at, or null when it can't be traced (shown in the banner). */
function formPathFor(path: CrmValidationIssue["path"], rowIndexMap: RowIndexMap): RecordFormPath | null {
  const [head, second, third, fourth] = path;
  if (head === "master") {
    return second === "long_name" || second === "short_name" ? `new_group.${second}` : "master_id";
  }
  if (head === "record" && typeof second === "string") {
    if (RECORD_BASE_FIELDS.has(second)) return second as RecordFormPath;
    if (second === "onboarding_instance_id") return "onboarding";
    if (second in recordFormFields) return `fields.${second}` as RecordFormPath;
    return null;
  }
  if (head !== "contacts" && head !== "addresses") return null;

  // Create lists items directly (["contacts", 0, ...]); apply-changes nests them (["contacts", "creates", 0, ...]).
  const [listPath, itemIndex, field] = typeof second === "number"
    ? [head, second, third]
    : [`${head}.${String(second)}`, third, fourth];
  const formIndex = typeof itemIndex === "number" ? rowIndexMap[listPath]?.[itemIndex] : undefined;
  if (formIndex === undefined) return null;
  return rowPath(head, formIndex, typeof field === "string" ? field : null);
}

const CONTACT_VALUE_PATH = /^contacts\.\d+\.value$/;

/** Whether a field error is a phone that can't be converted to E.164 (and so can't be saved). */
export function isInvalidPhoneError(name: string, message: string | undefined): boolean {
  return CONTACT_VALUE_PATH.test(name) && message === CRM_INVALID_PHONE_MESSAGE;
}

/** Banner for a save blocked by phones that can't be converted to E.164. */
export function invalidPhonesBanner(count: number): FormBanner {
  return {
    message: count === 1
      ? "This record wasn't saved: 1 phone number isn't valid. Fix the highlighted phone number, like (515) 555-0100, and try again."
      : `This record wasn't saved: ${count} phone numbers aren't valid. Fix the highlighted phone numbers, like (515) 555-0100, and try again.`,
  };
}

/** Maps body validation issues (the server's, or the request schema's) onto form fields. */
export function issuesToFormSubmitError(issues: CrmValidationIssue[], rowIndexMap: RowIndexMap): FormSubmitError {
  const fieldErrors: FormFieldError[] = [];
  const unmapped: string[] = [];
  for (const issue of issues) {
    const name = formPathFor(issue.path, rowIndexMap);
    if (name) {
      fieldErrors.push({ name, message: issue.message });
    } else {
      unmapped.push(issue.message);
    }
  }
  const invalidPhones = fieldErrors.filter((fieldError) => isInvalidPhoneError(fieldError.name, fieldError.message)).length;
  const banners = [
    ...(invalidPhones > 0 ? [invalidPhonesBanner(invalidPhones).message] : []),
    ...(unmapped.length > 0 ? [`This record couldn't be saved: ${unmapped.join(" ")}`] : []),
  ];
  return {
    banner: banners.length > 0 ? { message: banners.join(" ") } : null,
    fieldErrors,
  };
}

/** Puts a message on each matching row, on `field` (or the row itself when null). */
function rowErrors<Row>(
  section: RowSection,
  rows: Row[],
  matches: (row: Row) => boolean,
  field: string | null,
  message: (row: Row) => string,
): FormFieldError[] {
  return rows.flatMap((row, index) => (matches(row) ? [{ name: rowPath(section, index, field), message: message(row) }] : []));
}

const staleRowsBanner: FormBanner = {
  message: "Someone else changed this record after you opened it. Your changes haven't been saved.",
  offerReload: true,
};
export const genericFormSubmitError: FormSubmitError = {
  banner: { message: "Something went wrong saving this record. Try again." },
  fieldErrors: [],
};

/** Where a failed save's error shows: a banner for page-level problems, else the named field or row. */
export function toFormSubmitError(
  rules: CrmRules,
  result: FailedResult,
  values: RecordFormValues,
  rowIndexMap: RowIndexMap,
): FormSubmitError {
  if (isCrmAuthError(result)) {
    return { banner: { message: "You don't have permission to make this change." }, fieldErrors: [] };
  }

  const issues = crmValidationIssues(result.data);
  if (issues) return issuesToFormSubmitError(issues, rowIndexMap);

  const cantUseTags = (tagIds: string[]) => `These tags can't be used here: ${tagNames(rules, tagIds)}`;

  switch (crmErrorReason(result.data)) {
    case "crm_record_revision_conflict":
      return {
        banner: {
          message: "Someone else saved changes to this record after you opened it. Your changes haven't been saved.",
          offerReload: true,
        },
        fieldErrors: [],
      };
    case "crm_record_not_found":
      return { banner: { message: "This record no longer exists.", offerBackToList: true }, fieldErrors: [] };
    case "crm_record_master_not_found":
      return { banner: null, fieldErrors: [{ name: "master_id", message: "This record group no longer exists." }] };
    case "crm_record_owner_invalid":
      return { banner: null, fieldErrors: [{ name: "owner_id", message: "Choose a record this can belong to." }] };
    case "crm_record_client_id_taken":
      return {
        banner: { message: `A customer with client ID "${values.client_id.trim()}" already exists. Enter a different client ID.` },
        fieldErrors: [],
      };
    case "crm_record_onboarding_instance_invalid":
      return {
        banner: null,
        fieldErrors: [{ name: "onboarding", message: "This onboarding instance was already used or no longer exists. Choose another one." }],
      };
    case "crm_record_trader_invalid":
      return { banner: null, fieldErrors: [{ name: "fields.trader_id", message: "This trader doesn't exist. Choose another one." }] };
    case "crm_contacts_invalid": {
      const parsed = ContactIdsDataSchema.safeParse(result.data);
      const ids = new Set(parsed.success ? parsed.data.contact_ids : []);
      return {
        banner: staleRowsBanner,
        fieldErrors: rowErrors("contacts", values.contacts, (row) => row.id !== null && ids.has(row.id), null,
          () => "This contact was changed or removed by someone else."),
      };
    }
    case "crm_addresses_invalid": {
      const parsed = AddressIdsDataSchema.safeParse(result.data);
      const ids = new Set(parsed.success ? parsed.data.address_ids : []);
      return {
        banner: staleRowsBanner,
        fieldErrors: rowErrors("addresses", values.addresses, (row) => row.id !== null && ids.has(row.id), null,
          () => "This address was changed or removed by someone else."),
      };
    }
    case "crm_record_tags_invalid":
    case "crm_contact_tags_invalid":
    case "crm_address_tags_invalid": {
      const parsed = TagIdsDataSchema.safeParse(result.data);
      const invalid = new Set(parsed.success ? parsed.data.tag_ids : []);
      const invalidIn = (tagIds: string[]) => tagIds.filter((tagId) => invalid.has(tagId));
      const reason = crmErrorReason(result.data);
      const fieldErrors: FormFieldError[] = reason === "crm_record_tags_invalid"
        ? [{ name: "tag_ids", message: cantUseTags([...invalid]) }]
        : reason === "crm_contact_tags_invalid"
          ? rowErrors("contacts", values.contacts, (row) => invalidIn(row.tag_ids).length > 0, "tag_ids", (row) => cantUseTags(invalidIn(row.tag_ids)))
          : rowErrors("addresses", values.addresses, (row) => invalidIn(row.tag_ids).length > 0, "tag_ids", (row) => cantUseTags(invalidIn(row.tag_ids)));
      return {
        banner: fieldErrors.length > 0 ? null : { message: cantUseTags([...invalid]) },
        fieldErrors,
        refetchRules: true,
      };
    }
    case "crm_contact_tags_not_unique": {
      const parsed = DuplicateContactTagsDataSchema.safeParse(result.data);
      const duplicates = parsed.success ? parsed.data.duplicates : [];
      const duplicatedIn = (row: RecordFormValues["contacts"][number]) => duplicates
        .filter((duplicate) => duplicate.kind === row.kind && row.tag_ids.includes(duplicate.tag_id))
        .map((duplicate) => duplicate.tag_id);
      const fieldErrors = rowErrors("contacts", values.contacts, (row) => duplicatedIn(row).length > 0, "tag_ids",
        (row) => onePerContactKindMessage(rules, row.kind, duplicatedIn(row)));
      return {
        // No row matches when the clash is with a contact saved by someone else after the form loaded.
        banner: fieldErrors.length > 0 ? null : { message: "Only one contact of each kind can carry these tags." },
        fieldErrors,
      };
    }
    case "crm_record_type_immutable":
    case "crm_record_owner_immutable":
      return { banner: { message: "This record couldn't be saved. Reload the page and try again." }, fieldErrors: [] };
    default:
      return genericFormSubmitError;
  }
}
